/**
 * /superadmin entry point — Google-SSO-only login for the platform-admin
 * plane. If the user has already authed AND is allowlisted, we hand them
 * straight through to the console. Otherwise render the Sign-in with Google
 * button that kicks off /api/auth/superadmin/start.
 *
 * Security posture (Feb 2026):
 *   - Email allowlist is enforced SERVER-SIDE in the OAuth callback. The
 *     client never decides "are you a superadmin" — it only reflects state.
 *   - Query string `?error=not_authorized` surfaces unauthorized attempts
 *     without enumeration (same copy for "not a user" and "not allowlisted").
 *   - Every GET to /api/superadmin/me is audit-logged on the backend.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';
import SuperAdminConsolePage from './SuperAdminConsolePage';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function SuperAdminPage() {
  const [search] = useSearchParams();
  const error = search.get('error');
  const [status, setStatus] = useState('loading'); // loading | anonymous | authorized | forbidden
  const [me, setMe] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/superadmin/me`, { credentials: 'include' });
        if (!res.ok) {
          if (!cancelled) setStatus('anonymous');
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        if (data.is_superadmin) { setMe(data); setStatus('authorized'); }
        else if (data.authenticated) setStatus('forbidden');
        else setStatus('anonymous');
      } catch {
        if (!cancelled) setStatus('anonymous');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const beginLogin = () => {
    window.location.href = `${API}/api/auth/superadmin/start`;
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center orka-surface-page" data-testid="superadmin-loading">
        <Loader2 className="w-6 h-6 animate-spin orka-text-secondary" />
      </div>
    );
  }

  if (status === 'authorized' && me) {
    return <SuperAdminConsolePage me={me} />;
  }

  return (
    <div className="min-h-screen orka-surface-page flex items-center justify-center p-6" data-testid="superadmin-login">
      <div className="w-full max-w-md rounded-2xl border orka-border-default orka-surface-raised p-8 shadow-[var(--orka-shadow-lg)]">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl orka-bg-intent-soft flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" style={{ color: 'var(--orka-text-intent)' }} />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider orka-text-tertiary">Orka</div>
            <div className="text-lg font-semibold orka-text-primary">Superadmin</div>
          </div>
        </div>
        <p className="text-sm orka-text-secondary mb-6">
          Platform-admin plane. Google SSO only — restricted to a hard-coded allowlist.
          Every attempt is audit-logged.
        </p>

        {(error === 'not_authorized' || status === 'forbidden') && (
          <div
            className="rounded-lg p-3 mb-5 border orka-border-subtle orka-bg-danger-soft"
            data-testid="superadmin-error"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--orka-text-danger)' }} />
              <div className="text-xs orka-text-primary">
                <div className="font-semibold mb-0.5">Not authorized</div>
                <div className="orka-text-secondary">
                  This account is not on the superadmin allowlist. The attempt has been recorded.
                </div>
              </div>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={beginLogin}
          data-testid="superadmin-google-signin-btn"
          className="w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-lg border orka-border-default orka-surface-base hover:orka-surface-selected orka-transition-colors font-medium orka-text-primary"
        >
          <GoogleMark />
          <span>Sign in with Google</span>
        </button>

        <div className="mt-6 text-[11px] orka-text-tertiary leading-relaxed">
          You will be redirected to Google. Only <code className="font-mono">joseph.telfer13@gmail.com</code> is currently allowlisted.
          Other accounts that complete sign-in will be dropped to their normal session (if any) or rejected.
        </div>
      </div>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.2 6.4 29.4 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24s8.7 19.5 19.5 19.5c11 0 19-8 19-19.5 0-1.2-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.2 6.4 29.4 4.5 24 4.5 16 4.5 9.1 9 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 43.5c5.2 0 9.9-2 13.5-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.3 2.4-5.3 0-9.7-3.4-11.3-8l-6.5 5c2.8 5.7 9.7 11 17.8 11z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4 5.3l6.2 5.2c-.4.4 6.6-4.8 6.6-14.5 0-1.2-.1-2.3-.5-3.5z" />
    </svg>
  );
}
